import React, { useState, useEffect } from 'react';
import { KnownSite } from '../types/manga';
import { UserBackupData } from '../services/storage';
import { formatBackupDate } from '../utils/helpers';
import { showConfirm } from './ConfirmDialog';
import { X, Plus, Trash2, Globe, RotateCcw, Save, Download, Upload, Check } from 'lucide-react';

interface SiteManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sites: KnownSite[];
  onSaveSites: (sites: KnownSite[]) => void;
  onResetAllManga?: () => void;
  backupInfo?: UserBackupData | null;
  onSaveBackup?: () => void;
  onRestoreBackup?: () => void;
  onExportBackup?: () => void;
  onImportBackup?: (file: File) => void;
}

export const SiteManagerModal: React.FC<SiteManagerModalProps> = ({
  isOpen,
  onClose,
  sites,
  onSaveSites,
  onResetAllManga,
  backupInfo,
  onSaveBackup,
  onRestoreBackup,
  onExportBackup,
  onImportBackup,
}) => {
  const [localSites, setLocalSites] = useState<KnownSite[]>([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setLocalSites(sites.length > 0 ? [...sites] : [{ name: '', url: '' }]);
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, sites]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    setLocalSites([...localSites, { name: '', url: '' }]);
  };

  const handleRemoveRow = (index: number) => {
    setLocalSites(localSites.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof KnownSite, value: string) => {
    const updated = [...localSites];
    updated[index][field] = value;
    if (field === 'url' && !updated[index].name && value) {
      try {
        const u = new URL(value);
        updated[index].name = u.hostname.replace(/^www\./, '');
      } catch {}
    }
    setLocalSites(updated);
  };

  const handleSave = () => {
    const cleaned = localSites
      .filter((s) => s.url.trim() !== '')
      .map((s) => {
        const cleanUrl = s.url.trim().replace(/\/+$/, '');
        return {
          name: s.name.trim() || cleanUrl.replace(/^https?:\/\/(www\.)?/, ''),
          url: cleanUrl,
        };
      });

    onSaveSites(cleaned);
    onClose();
  };

  return (
    <div className="modal-overlay safe-area-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-lg">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">เว็บสำหรับค้นหาและตรวจเช็ค</h3>
          <button onClick={onClose} title="ปิด" className="icon-btn -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content with smooth native scroll */}
        <div className="modal-body space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          <section className="space-y-3">
            <p className="text-xs text-zinc-500 leading-relaxed">
              รายการเว็บสำหรับตรวจเช็คตอนใหม่อัตโนมัติ ระบบจะค้นหาและดึงข้อมูลจากเว็บเหล่านี้เพื่ออัปเดตตอนล่าสุดให้คุณ
            </p>

            <div className="space-y-2">
              {localSites.map((site, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={site.name}
                    placeholder="ชื่อเว็บ (เช่น 1668manga)"
                    onChange={(e) => handleChange(idx, 'name', e.target.value)}
                    className="field w-1/3 text-xs sm:text-sm"
                  />
                  <input
                    type="url"
                    value={site.url}
                    placeholder="https://1668manga.com"
                    onChange={(e) => handleChange(idx, 'url', e.target.value)}
                    className="field flex-1 w-auto text-xs sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveRow(idx)}
                    title="ลบเว็บนี้"
                    className="icon-btn hover:text-rose-400 hover:bg-rose-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddRow}
              className="w-full h-9 rounded-lg border border-dashed border-white/10 hover:border-white/25 text-zinc-500 hover:text-zinc-200 text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>เพิ่มเว็บใหม่</span>
            </button>
          </section>

          {/* 1. Custom User Snapshot Backup */}
          <section className="border-t border-white/[0.06] pt-5">
            <h4 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Save className="w-4 h-4 text-zinc-500" />
              <span>จุดสำรองข้อมูล</span>
            </h4>
            <p className="text-xs text-zinc-500 mt-1.5 mb-3 leading-relaxed">
              บันทึกสถานะปัจจุบันทั้งหมด (รายชื่อเรื่อง, ลิงก์ทุกเว็บ, จำนวนตอน, แรงค์ และเว็บค้นหา) ไว้เป็นจุดสำรองของคุณเอง หากแก้ไขอะไรพลาด สามารถกดกู้คืนกลับมาได้ทันที
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {onSaveBackup && (
                <button type="button" onClick={onSaveBackup} className="btn btn-sm btn-primary">
                  <Save className="w-3.5 h-3.5" />
                  <span>บันทึกจุดสำรองตอนนี้</span>
                </button>
              )}

              {onRestoreBackup && backupInfo && (
                <button
                  type="button"
                  onClick={async () => {
                    if (
                      await showConfirm({
                        title: 'กู้คืนข้อมูลจากจุดสำรอง?',
                        message: `ข้อมูลจะย้อนกลับไปเป็นตอน ${formatBackupDate(backupInfo.date)} (${backupInfo.count} เรื่อง)`,
                        confirmLabel: 'กู้คืน',
                        danger: true,
                      })
                    ) {
                      onRestoreBackup();
                      onClose();
                    }
                  }}
                  className="btn btn-sm btn-secondary"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>กู้คืนจากจุดสำรอง</span>
                </button>
              )}
            </div>

            {backupInfo ? (
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-400">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>
                  จุดสำรองล่าสุด <span className="text-zinc-200">{formatBackupDate(backupInfo.date)}</span> (
                  {backupInfo.count} เรื่อง)
                </span>
              </div>
            ) : (
              <div className="mt-3 text-[11px] text-zinc-500">ยังไม่มีการบันทึกจุดสำรอง</div>
            )}
          </section>

          {/* 2. File Download / Upload (JSON) */}
          <section className="border-t border-white/[0.06] pt-5 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-zinc-400">สำรองเป็นไฟล์ JSON</span>
            <div className="flex items-center gap-1.5">
              {onExportBackup && (
                <button
                  type="button"
                  onClick={onExportBackup}
                  title="ดาวน์โหลดไฟล์ .json สำรองไว้ในเครื่อง"
                  className="btn btn-sm btn-secondary"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>ดาวน์โหลด</span>
                </button>
              )}

              {onImportBackup && (
                <label title="นำเข้าไฟล์ .json ที่เคยสำรองไว้" className="btn btn-sm btn-secondary cursor-pointer">
                  <Upload className="w-3.5 h-3.5" />
                  <span>นำเข้า</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onImportBackup(file);
                        e.target.value = '';
                        onClose();
                      }
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </section>

          {/* 3. Factory Reset to System Defaults */}
          {onResetAllManga && (
            <section className="border-t border-white/[0.06] pt-5">
              <h4 className="text-sm font-semibold text-zinc-100">รีเซ็ตแหล่งข้อมูลเว็บ</h4>
              <p className="text-xs text-zinc-500 mt-1.5 mb-3 leading-relaxed">
                หากลิงก์เว็บเกิดความผิดเพี้ยน สามารถรีเซ็ตแหล่งข้อมูลเว็บกลับเป็นค่ามาตรฐานระบบได้ (คงจำนวนตอนและแรงค์ไว้)
              </p>
              <button
                type="button"
                onClick={async () => {
                  if (
                    await showConfirm({
                      title: 'รีเซ็ตแหล่งข้อมูลเว็บ?',
                      message: 'ลิงก์เว็บจะกลับเป็นค่าเริ่มต้นของระบบ\nจำนวนตอนล่าสุด แรงค์ที่จัดไว้ และเรื่องที่เพิ่มใหม่จะยังอยู่ครบ',
                      confirmLabel: 'รีเซ็ต',
                      danger: true,
                    })
                  ) {
                    onResetAllManga();
                    onClose();
                  }
                }}
                className="btn btn-sm btn-danger ring-1 ring-inset ring-rose-500/25"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>รีเซ็ตเป็นค่าเริ่มต้นระบบ</span>
              </button>
            </section>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-ghost">
            ยกเลิก
          </button>
          <button type="button" onClick={handleSave} className="btn btn-primary px-5">
            บันทึก
          </button>
        </div>
      </div>
    </div>
  );
};
